import React from "react";
import { Link } from "react-router-dom";
import {
  ChevronRight,
  Calendar,
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import MainPageWrapper from "@/components/MainPageWrapper/MainPageWrapper";
import HubHero from "@/components/HubHero/HubHero";
import { useHubDashboard } from "./hooks/useHubDashboard";
import { MinimalResourceCard } from "./components/MinimalResourceCard";
import { format } from "date-fns";

const HubDashboard: React.FC = () => {
  const { news, featuredResources, favorites, loading, error } =
    useHubDashboard();

  return (
    <div className="relative">
      {/* Hero Section - Full Width */}
      <HubHero />

      {/* Content Sections - With Standard Padding */}
      <MainPageWrapper
        error={error}
        loading={loading}
        hideHeader
      >
        <div className="space-y-16 md:space-y-20 pt-8 md:pt-12">
        {/* Featured News Section */}
        {news && news.filter((item) => item.isFeatured).length > 0 && (
          <section>
            <div className="mb-6">
              <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-gray-500">
                Member News
              </h2>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {news
                    .filter((item) => item.isFeatured)
                    .map((item) => (
                      <Link
                        className="block hover:no-underline"
                        key={item.id}
                        to={`/hub/resources/${item.slug}`}
                      >
                        <Card className="h-full overflow-hidden border-gray-100 dark:border-gray-800 hover:shadow-sm transition-all duration-300">
                          {item.thumbnailUrl && (
                            <div className="aspect-video w-full overflow-hidden">
                              <img
                                alt={item.title}
                                className="h-full w-full object-cover"
                                src={item.thumbnailUrl}
                              />
                            </div>
                          )}
                          <CardContent className="p-6">
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 font-light">
                              <Calendar className="h-3 w-3" />
                              {item.createdOrPublishStartDate && (
                                <span>
                                  {format(
                                    new Date(item.createdOrPublishStartDate),
                                    "MMM d, yyyy",
                                  )}
                                </span>
                              )}
                            </div>
                            <h3 className="text-base font-medium mb-2 line-clamp-2 text-gray-900 dark:text-white">
                              {item.title}
                            </h3>
                            <div className="mt-4 text-gray-600 dark:text-gray-400 text-sm font-light flex items-center gap-1 border-b border-gray-300 dark:border-gray-700 inline-flex">
                              Read more
                              <ChevronRight className="h-3 w-3" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
            </div>
          </section>
        )}

        {/* Recently Added Section */}
        {news && news.filter((item) => !item.isFeatured).length > 0 && (
          <section>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-gray-500">
                Featured
              </h2>
              <Link
                to="/hub/news"
                className="text-gray-500 hover:text-gray-700 text-xs font-medium flex items-center gap-1 transition-colors"
              >
                View All
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
                  {news
                    .filter((item) => !item.isFeatured)
                    .slice(0, 3)
                    .map((item) => (
                      <Link
                        className="block hover:no-underline"
                        key={item.id}
                        to={`/hub/resources/${item.slug}`}
                      >
                        <Card className="h-full overflow-hidden border-gray-100 dark:border-gray-800 hover:shadow-sm transition-all duration-300">
                          {item.thumbnailUrl && (
                            <div className="aspect-video w-full overflow-hidden">
                              <img
                                alt={item.title}
                                className="h-full w-full object-cover"
                                src={item.thumbnailUrl}
                              />
                            </div>
                          )}
                          <CardContent className="p-6">
                            <div className="flex items-center gap-2 text-xs text-gray-500 mb-3 font-light">
                              <Calendar className="h-3 w-3" />
                              {item.createdOrPublishStartDate && (
                                <span>
                                  {format(
                                    new Date(item.createdOrPublishStartDate),
                                    "MMM d, yyyy",
                                  )}
                                </span>
                              )}
                            </div>
                            <h3 className="text-base font-medium mb-2 line-clamp-2 text-gray-900 dark:text-white">
                              {item.title}
                            </h3>
                            <div className="mt-4 text-gray-600 dark:text-gray-400 text-sm font-light flex items-center gap-1 border-b border-gray-300 dark:border-gray-700 inline-flex">
                              Read more
                              <ChevronRight className="h-3 w-3" />
                            </div>
                          </CardContent>
                        </Card>
                      </Link>
                    ))}
            </div>
          </section>
        )}

        {/* Featured Resources Section */}
        {featuredResources && featuredResources.length > 0 && (
          <section>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-gray-500">
                Featured Resources
              </h2>
              <Link
                to="/hub/resources"
                className="text-gray-500 hover:text-gray-700 text-xs font-medium flex items-center gap-1 transition-colors"
              >
                View All
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {featuredResources.map((resource) => (
                <MinimalResourceCard
                  key={resource.id}
                  resource={resource}
                />
              ))}
            </div>
          </section>
        )}

        {/* User Favorites Section */}
        {favorites && favorites.length > 0 && (
          <section>
            <div className="mb-6 flex items-center justify-between">
              <h2 className="text-xs font-medium tracking-[0.2em] uppercase text-gray-500">
                Your Favorites
              </h2>
              <Link
                to="/hub/resources?showFavorites=true"
                className="text-gray-500 hover:text-gray-700 text-xs font-medium flex items-center gap-1 transition-colors"
              >
                View All
                <ChevronRight className="h-3 w-3" />
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {favorites.map((resource) => (
                <MinimalResourceCard key={resource.id} resource={resource} />
              ))}
            </div>
          </section>
        )}

        {/* Empty State */}
        {(!news || news.length === 0) &&
          (!featuredResources || featuredResources.length === 0) &&
          (!favorites || favorites.length === 0) && (
            <div className="text-center py-12">
              <p className="text-muted-foreground">
                No content available. Check back later for updates tailored to
                your trade specialization.
              </p>
            </div>
          )}
        </div>
      </MainPageWrapper>
    </div>
  );
};

export default HubDashboard;
